import { useEffect, useMemo, useState } from 'react'
import services from '../../services'

/** O que o dicionário do relatório libera mas o modelo do Power BI não tem mais (renomeado ou removido lá). */
export interface ItensAusentes {
  colunas: string[]
  medidas: string[]
}

export const SEM_AUSENTES: ItensAusentes = { colunas: [], medidas: [] }

interface DicionarioMinimo {
  colunas: { nome: string }[]
  medidas: { nome: string }[]
}

const igual = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** "dUsuarios[telefone]" → "dUsuarios". */
export const tabelaDaColuna = (nome: string): string => {
  const i = nome.indexOf('[')
  return i > 0 ? nome.slice(0, i) : ''
}

/**
 * Compara o dicionário com o que o modelo tem. Coluna some quando a tabela some ou quando a tabela não tem mais a coluna;
 * medida some quando o modelo não a lista. A caixa não conta (o DAX também não liga para ela).
 */
export function calcularAusentes(
  dic: DicionarioMinimo,
  tabelasDoModelo: string[],
  colunasPorTabela: Record<string, string[]>,
  medidasDoModelo: string[]
): ItensAusentes {
  const colunas = dic.colunas.map(c => c.nome).filter(nome => {
    const tabela = tabelaDaColuna(nome)
    const tabelaNoModelo = tabelasDoModelo.find(t => igual(t, tabela))
    if (!tabelaNoModelo) return true
    const doModelo = colunasPorTabela[tabelaNoModelo] ?? colunasPorTabela[tabela] ?? []
    return !doModelo.some(c => igual(c, nome))
  })
  const medidas = dic.medidas.map(m => m.nome).filter(nome => !medidasDoModelo.some(m => igual(m, nome)))
  return { colunas, medidas }
}

export interface ConferenciaDoModelo {
  ausentes: ItensAusentes
  /** A conferência terminou (com ou sem itens ausentes). */
  conferido: boolean
  /** Não deu para ler o modelo: sem isso não dá para dizer o que sumiu. */
  falhou: boolean
}

/**
 * Lê o modelo do relatório (tabelas, medidas e as colunas das tabelas que o dicionário usa) e diz o que o dicionário libera
 * que o modelo não tem mais. Refaz quando o dicionário muda (salvar o dicionário recarrega a página e traz um novo).
 */
export function useItensAusentes(dashboardId: number, dic: DicionarioMinimo | undefined): ConferenciaDoModelo {
  const [estado, setEstado] = useState<ConferenciaDoModelo>({ ausentes: SEM_AUSENTES, conferido: false, falhou: false })
  // Muda só quando os nomes mudam, não a cada objeto novo.
  const chave = useMemo(() => (dic ? JSON.stringify([dic.colunas.map(c => c.nome), dic.medidas.map(m => m.nome)]) : ''), [dic])

  useEffect(() => {
    if (!dic || !dashboardId) return
    let vivo = true
    ;(async () => {
      const ler = async (parte: string, tabela?: string): Promise<{ nome: string }[] | null> => {
        try {
          const r: any = await services.api.relatorioHtmlModelo(dashboardId, parte, tabela)
          return r.fail || !r.data?.ok ? null : r.data.itens
        } catch {
          return null
        }
      }
      const [tabelas, medidas] = await Promise.all([ler('tabelas'), ler('medidas')])
      if (!vivo) return
      if (!tabelas || !medidas) { setEstado({ ausentes: SEM_AUSENTES, conferido: true, falhou: true }); return }

      const nomesDeTabela = tabelas.map(t => t.nome)
      const usadas = Array.from(new Set(dic.colunas.map(c => tabelaDaColuna(c.nome)).filter(Boolean)))
        .map(t => nomesDeTabela.find(n => igual(n, t)))
        .filter((t): t is string => !!t)
      const lidas = await Promise.all(usadas.map(async t => [t, await ler('colunas', t)] as const))
      if (!vivo) return
      // Tabela existe mas as colunas não vieram: não dá para afirmar nada sobre ela.
      if (lidas.some(([, cols]) => cols === null)) { setEstado({ ausentes: SEM_AUSENTES, conferido: true, falhou: true }); return }
      const colunasPorTabela = Object.fromEntries(lidas.map(([t, cols]) => [t, (cols ?? []).map(c => c.nome)]))

      setEstado({
        ausentes: calcularAusentes(dic, nomesDeTabela, colunasPorTabela, medidas.map(m => m.nome)),
        conferido: true,
        falhou: false
      })
    })()
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, chave])

  return estado
}
